import { useEffect, useState } from 'react';
import { School } from '../../../types/School';
import { User } from '../../../types/User';
import {
  fetchSchools,
  updateSchoolDomains,
  updateSchoolShortName,
  getStudentsBySchool,
  verifySchool,
} from '../../../services/schools';
import Modal from '@mui/material/Modal';
import { Edit3, Image, Eye, AtSign, CheckCircle } from 'react-feather';
import SchoolPictureUploader from './SchoolPictureUploader';
import Tooltip from '@mui/material/Tooltip';

const SchoolDashboard: React.FC = () => {
  const [schoolList, setSchoolList] = useState<School[]>([]);
  const [unverifiedSchoolList, setUnverifiedSchoolList] = useState<School[]>(
    []
  );
  const [showUpdateDomainsModal, setShowUpdateDomainsModal] = useState(false);
  const [schoolImageId, setSchoolImageId] = useState<string>('');
  const [showSchoolImageModal, setShowSchoolImageModal] = useState(false);
  const [showUpdateShortNameModal, setShowUpdateShortNameModal] =
    useState(false);
  const [showStudentsModal, setShowStudentsModal] = useState(false);
  const [selectedSchoolStudents, setSelectedSchoolStudents] = useState<User[]>(
    []
  );
  const [pendingUpdateSchoolShortName, setPendingUpdateSchoolShortName] =
    useState<string>('');
  const [pendingUpdateSchoolId, setPendingUpdateSchoolId] = useState<
    string | null
  >(null);
  const [pendingUpdateSchoolDomains, setPendingUpdateSchoolDomains] =
    useState<string>('');

  useEffect(() => {
    loadSchools();
  }, []);

  useEffect(() => {
    if (!showSchoolImageModal) {
      setSchoolImageId('');
    }
  }, [showSchoolImageModal]);

  const loadSchools = async () => {
    const schools = await fetchSchools();
    if (schools) {
      const verifiedSchools = schools.filter((school) => school.verified);
      const unverifiedSchools = schools.filter((school) => !school.verified);
      const sortedVerifiedSchools = [...verifiedSchools].sort(
        (a, b) => (b.studentCount || 0) - (a.studentCount || 0)
      );
      setSchoolList(sortedVerifiedSchools);
      setUnverifiedSchoolList([...unverifiedSchools]);
    }
  };

  const submitSchoolDomains = async () => {
    const domains = pendingUpdateSchoolDomains.split(',');
    if (domains.length > 0 && pendingUpdateSchoolId) {
      const trimmedDomains = domains.map((domain) => domain.trim());
      await updateSchoolDomains(pendingUpdateSchoolId, trimmedDomains);
      setShowUpdateDomainsModal(false);
      setPendingUpdateSchoolDomains('');
      setPendingUpdateSchoolId(null);
      loadSchools();
    }
  };

  const submitSchoolShortName = async () => {
    if (pendingUpdateSchoolId) {
      await updateSchoolShortName(
        pendingUpdateSchoolId,
        pendingUpdateSchoolShortName
      );
      setShowUpdateShortNameModal(false);
      setPendingUpdateSchoolShortName('');
      setPendingUpdateSchoolId(null);
      loadSchools();
    }
  };

  const handleViewStudents = async (schoolId: string) => {
    const students = await getStudentsBySchool(schoolId);
    setSelectedSchoolStudents(students);
    setShowStudentsModal(true);
  };

  const handleCloseStudentsModal = () => {
    setShowStudentsModal(false);
    setSelectedSchoolStudents([]);
  };

  const handleVerifySchool = async (schoolId: string) => {
    await verifySchool(schoolId);
    loadSchools();
  };

  return (
    <>
      <table className="users-list">
        <thead>
          <tr>
            <th>Name</th>
            <th style={{ width: '10rem' }}>Short Name</th>
            <th>Domains</th>
            <th>Cohort</th>
            <th>Users</th>
            <th>Picture</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {schoolList?.map((school: School) => (
            <tr className="school-row" key={school.id}>
              <td>{school.name}</td>
              <td>{school.shortName}</td>
              <td>{school.domains?.join(', ')}</td>
              <td>{school.cohort}</td>
              <td>{school.studentCount}</td>
              <td>
                {school.image && (
                  <div
                    className="school-picture-container"
                    style={{ height: '1rem', width: '1rem' }}
                  >
                    <a
                      href={school.image}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <img
                        src={school.image}
                        alt={school.name}
                        style={{
                          height: '100%',
                          width: '100%',
                          objectFit: 'cover',
                        }}
                      />
                    </a>
                  </div>
                )}
              </td>
              <td style={{ display: 'flex', gap: '0.5rem' }}>
                <Tooltip title="Change School Short Name">
                  <button
                    className="button button--icon-only"
                    onClick={() => {
                      setPendingUpdateSchoolId(school.id);
                      setPendingUpdateSchoolShortName(school.shortName || '');
                      setShowUpdateShortNameModal(true);
                    }}
                  >
                    <Edit3 />
                  </button>
                </Tooltip>
                <Tooltip title="Change School Domains">
                  <button
                    className="button button--icon-only"
                    onClick={() => {
                      setPendingUpdateSchoolId(school.id);
                      setPendingUpdateSchoolDomains(
                        school.domains?.join(', ') || ''
                      );
                      setShowUpdateDomainsModal(true);
                    }}
                  >
                    <AtSign />
                  </button>
                </Tooltip>
                <Tooltip title="View or Change School Picture">
                  <button
                    className="button button--icon-only"
                    onClick={() => {
                      setSchoolImageId(school.id);
                      setShowSchoolImageModal(true);
                    }}
                  >
                    <Image />
                  </button>
                </Tooltip>
                <Tooltip title="View Students">
                  <button
                    className="button button--icon-only"
                    onClick={() => handleViewStudents(school.id)}
                  >
                    <Eye />
                  </button>
                </Tooltip>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {unverifiedSchoolList.length > 0 && (
        <div
          className="unverified-schools-container"
          style={{ marginTop: '1rem' }}
        >
          <h2>Unverified Schools</h2>
          <table className="users-list">
            <thead>
              <tr>
                <th>Name</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {unverifiedSchoolList.map((school) => (
                <tr key={school.id}>
                  <td>{school.name}</td>
                  <td>
                    <Tooltip title="Verify School">
                      <button
                        className="button button--icon-only"
                        onClick={() => handleVerifySchool(school.id)}
                      >
                        <CheckCircle />
                      </button>
                    </Tooltip>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {showUpdateDomainsModal && (
        <Modal
          open={showUpdateDomainsModal}
          onClose={() => setShowUpdateDomainsModal(false)}
        >
          <div className="modal">
            <div className="modal_header">
              <h2>Update Domains</h2>
            </div>
            <div className="modal_content">
              <input
                type="text"
                value={pendingUpdateSchoolDomains}
                onChange={(e) => setPendingUpdateSchoolDomains(e.target.value)}
              />
            </div>
            <div className="modal_footer">
              <button onClick={submitSchoolDomains}>Submit</button>
            </div>
          </div>
        </Modal>
      )}
      {showUpdateShortNameModal && (
        <Modal
          open={showUpdateShortNameModal}
          onClose={() => setShowUpdateShortNameModal(false)}
        >
          <div className="modal">
            <div className="modal_header">
              <h2>Update Short Name</h2>
            </div>
            <div className="modal_content">
              <input
                type="text"
                value={pendingUpdateSchoolShortName}
                onChange={(e) =>
                  setPendingUpdateSchoolShortName(e.target.value)
                }
              />
            </div>
            <div className="modal_footer">
              <button onClick={submitSchoolShortName}>Submit</button>
            </div>
          </div>
        </Modal>
      )}
      {showStudentsModal && (
        <Modal open={showStudentsModal} onClose={handleCloseStudentsModal}>
          <div className="modal">
            <div className="modal_header">
              <h2>School Students</h2>
            </div>
            <div className="modal_content">
              <table className="users-list">
                <thead>
                  <tr>
                    <th>Email</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedSchoolStudents.map((student) => (
                    <tr key={student.id}>
                      <td>{student.email || 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="modal_footer" style={{ padding: '1rem' }}>
              <button
                style={{ display: 'block', marginLeft: 'auto' }}
                onClick={handleCloseStudentsModal}
              >
                Close
              </button>
            </div>
          </div>
        </Modal>
      )}
      <SchoolPictureUploader
        schoolId={schoolImageId}
        isDialogOpen={showSchoolImageModal}
        setIsDialogOpen={setShowSchoolImageModal}
        loadSchools={loadSchools}
      />
    </>
  );
};

export default SchoolDashboard;
